import { useEffect, useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [regionAnalytics, setRegionAnalytics] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [emailAlerts, setEmailAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    days: 30,
    maxConfidence: 0.5
  });

  // Check if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    // Add dummy data for demonstration
    setStats({
      total_users: 1847,
      total_documents: 12456,
      total_flags: 342,
      recent_flags: 28,
      document_stats: [
        { verification_status: 'verified', count: 8934 },
        { verification_status: 'flagged', count: 342 },
        { verification_status: 'pending', count: 3180 }
      ],
      zimbabwe_stats: {
        flagged_users: 89,
        avg_confidence: 0.76,
        total_incidents: 342
      }
    });

    setHeatmapData([
      { latitude: -17.8315, longitude: 31.0361, confidence: 0.23, timestamp: '2024-10-14T10:30:00Z', address: 'Marondera, Zimbabwe' },
      { latitude: -17.8277, longitude: 31.0447, confidence: 0.18, timestamp: '2024-10-14T09:45:00Z', address: 'Rusape, Zimbabwe' },
      { latitude: -18.9715, longitude: 32.6612, confidence: 0.41, timestamp: '2024-10-14T08:20:00Z', address: 'Harare CBD, Zimbabwe' },
      { latitude: -20.1474, longitude: 28.5906, confidence: 0.33, timestamp: '2024-10-13T16:15:00Z', address: 'Bulawayo, Zimbabwe' },
      { latitude: -18.9645, longitude: 32.7700, confidence: 0.28, timestamp: '2024-10-13T14:30:00Z', address: 'Chitungwiza, Zimbabwe' }
    ]);

    setRegionAnalytics([
      { province: 'Harare', incident_count: 89, unique_users: 234, avg_confidence: 0.72, center_lat: -17.8252, center_lng: 31.0335 },
      { province: 'Bulawayo', incident_count: 67, unique_users: 187, avg_confidence: 0.68, center_lat: -20.1474, center_lng: 28.5906 },
      { province: 'Manicaland', incident_count: 45, unique_users: 123, avg_confidence: 0.74, center_lat: -18.9756, center_lng: 32.6612 },
      { province: 'Mashonaland Central', incident_count: 34, unique_users: 98, avg_confidence: 0.71, center_lat: -16.5500, center_lng: 31.2833 },
      { province: 'Masvingo', incident_count: 28, unique_users: 76, avg_confidence: 0.69, center_lat: -20.0758, center_lng: 30.8267 }
    ]);

    setFlaggedUsers([
      { username: 'john_doe_zw', flag_count: 7, avg_confidence: 0.23, last_incident: '2024-10-14T10:30:00Z', last_login_address: 'Harare, Zimbabwe' },
      { username: 'jane_smith_mw', flag_count: 5, avg_confidence: 0.31, last_incident: '2024-10-14T09:15:00Z', last_login_address: 'Bulawayo, Zimbabwe' },
      { username: 'mike_brown_zw', flag_count: 4, avg_confidence: 0.28, last_incident: '2024-10-13T16:45:00Z', last_login_address: 'Mutare, Zimbabwe' },
      { username: 'alice_jones_zw', flag_count: 6, avg_confidence: 0.19, last_incident: '2024-10-13T14:20:00Z', last_login_address: 'Gweru, Zimbabwe' }
    ]);

    setEmailAlerts([
      { id: 1, contact_name: 'Zimbabwe Police HQ', email_address: 'alerts@zrp.gov.zw', alert_types: 'high_risk,suspicious_activity', is_active: true, created_at: '2024-10-01T00:00:00Z' },
      { id: 2, contact_name: 'Security Admin', email_address: 'security@cybersafe.zw', alert_types: 'system_alerts', is_active: true, created_at: '2024-10-01T00:00:00Z' },
      { id: 3, contact_name: 'Emergency Response', email_address: 'emergency@gov.zw', alert_types: 'critical_threats', is_active: false, created_at: '2024-09-15T00:00:00Z' }
    ]);

    setLoading(false);
    // Uncomment to fetch real data when backend is ready
    // fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all admin data
      const [statsRes, heatmapRes, regionsRes, usersRes, emailRes] = await Promise.all([
        api.get('/api/admin/dashboard/stats'),
        api.get(`/api/admin/analytics/heatmap?days=${filters.days}&confidence_max=${filters.maxConfidence}`),
        api.get('/api/admin/analytics/regions'),
        api.get('/api/admin/users/flagged'),
        api.get('/api/admin/email-alerts')
      ]);

      setStats(statsRes.data);
      setHeatmapData(heatmapRes.data.heatmap_data || []);
      setRegionAnalytics(regionsRes.data.region_analytics || []);
      setFlaggedUsers(usersRes.data.flagged_users || []);
      setEmailAlerts(emailRes.data.email_alerts || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchDashboardData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border animate-spin w-8 h-8 border-4 border-green-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-300 font-mono">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md shadow-lg border-b border-green-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-green-400 font-mono">
              <span className="text-blue-400">[</span>Admin Dashboard<span className="text-blue-400">]</span>
            </h1>
            <p className="text-gray-300 font-mono">
              <span className="text-green-500">#</span> Zimbabwe Digital Identity Verification Analytics
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="border-b border-green-500/30">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: '📊' },
              { id: 'heatmap', name: 'Heatmap', icon: '🗺️' },
              { id: 'regions', name: 'Regions', icon: '📍' },
              { id: 'users', name: 'Flagged Users', icon: '👥' },
              { id: 'emails', name: 'Email Alerts', icon: '📧' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-mono text-sm transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'border-green-400 text-green-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-green-500/50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'overview' && (
            <OverviewTab stats={stats} />
          )}
          {activeTab === 'heatmap' && (
            <HeatmapTab 
              data={heatmapData} 
              filters={filters} 
              onFilterChange={handleFilterChange} 
            />
          )}
          {activeTab === 'regions' && (
            <RegionsTab data={regionAnalytics} />
          )}
          {activeTab === 'users' && (
            <UsersTab data={flaggedUsers} />
          )}
          {activeTab === 'emails' && (
            <EmailAlertsTab data={emailAlerts} refreshData={fetchDashboardData} />
          )}
        </div>
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
    <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-green-400/50 transition-all duration-300">
      <div className="flex items-center">
        <div className="text-3xl">👥</div>
        <div className="ml-4">
          <p className="text-sm font-mono text-gray-400">Total Users</p>
          <p className="text-2xl font-mono text-green-400">{stats?.total_users?.toLocaleString() || 0}</p>
        </div>
      </div>
    </div>
    
    <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-blue-400/50 transition-all duration-300">
      <div className="flex items-center">
        <div className="text-3xl">📄</div>
        <div className="ml-4">
          <p className="text-sm font-mono text-gray-400">Documents</p>
          <p className="text-2xl font-mono text-blue-400">{stats?.total_documents?.toLocaleString() || 0}</p>
        </div>
      </div>
    </div>
    
    <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-red-400/50 transition-all duration-300">
      <div className="flex items-center">
        <div className="text-3xl">🚩</div>
        <div className="ml-4">
          <p className="text-sm font-mono text-gray-400">Red Flags</p>
          <p className="text-2xl font-mono text-red-400">{stats?.total_flags || 0}</p>
        </div>
      </div>
    </div>
    
    <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-orange-400/50 transition-all duration-300">
      <div className="flex items-center">
        <div className="text-3xl">⚠️</div>
        <div className="ml-4">
          <p className="text-sm font-mono text-gray-400">Recent Flags</p>
          <p className="text-2xl font-mono text-orange-400">{stats?.recent_flags || 0}</p>
        </div>
      </div>
    </div>
    
    {/* Additional Security Metrics */}
    <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-purple-400/50 transition-all duration-300">
      <div className="flex items-center">
        <div className="text-3xl">🛡️</div>
        <div className="ml-4">
          <p className="text-sm font-mono text-gray-400">Security Score</p>
          <p className="text-2xl font-mono text-purple-400">89.2%</p>
        </div>
      </div>
    </div>
    
    <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-yellow-400/50 transition-all duration-300">
      <div className="flex items-center">
        <div className="text-3xl">🎯</div>
        <div className="ml-4">
          <p className="text-sm font-mono text-gray-400">Threat Level</p>
          <p className="text-2xl font-mono text-yellow-400">MEDIUM</p>
        </div>
      </div>
    </div>
    
    <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-cyan-400/50 transition-all duration-300">
      <div className="flex items-center">
        <div className="text-3xl">🔍</div>
        <div className="ml-4">
          <p className="text-sm font-mono text-gray-400">Active Scans</p>
          <p className="text-2xl font-mono text-cyan-400">147</p>
        </div>
      </div>
    </div>
    
    <div className="bg-black/90 backdrop-blur-md border border-green-500/30 rounded-lg shadow-lg p-6 hover:border-pink-400/50 transition-all duration-300">
      <div className="flex items-center">
        <div className="text-3xl">🚨</div>
        <div className="ml-4">
          <p className="text-sm font-mono text-gray-400">Police Alerts</p>
          <p className="text-2xl font-mono text-pink-400">23</p>
        </div>
      </div>
    </div>
  </div>
);

// Heatmap Tab Component
const HeatmapTab = ({ data, filters, onFilterChange }) => (
  <div>
    {/* Filters */}
    <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
      <h3 className="text-lg font-medium mb-4">Heatmap Filters</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Time Period (Days)</label>
          <select 
            value={filters.days}
            onChange={(e) => onFilterChange({ ...filters, days: parseInt(e.target.value) })}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Max Confidence</label>
          <select 
            value={filters.maxConfidence}
            onChange={(e) => onFilterChange({ ...filters, maxConfidence: parseFloat(e.target.value) })}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value={0.3}>Below 30%</option>
            <option value={0.5}>Below 50%</option>
            <option value={0.7}>Below 70%</option>
            <option value={1.0}>All confidence levels</option>
          </select>
        </div>
      </div>
    </div>
    
    {/* Heatmap Data */}
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <h3 className="text-lg font-medium mb-4">Suspicious Activity Heatmap Data</h3>
      <p className="text-gray-600 mb-4">Total incidents: {data.length}</p>
      
      {data.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {data.map((point, index) => (
            <div key={index} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">{point.full_name} (@{point.username})</p>
                  <p className="text-sm text-gray-600">{point.address || 'Unknown location'}</p>
                  <p className="text-sm text-red-600">Confidence: {(point.confidence * 100).toFixed(1)}%</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{new Date(point.timestamp).toLocaleDateString()}</p>
                  <p className="text-xs text-gray-400">
                    {point.latitude.toFixed(4)}, {point.longitude.toFixed(4)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-8">No suspicious activities found for the selected criteria.</p>
      )}
    </div>
  </div>
);

// Regions Tab Component
const RegionsTab = ({ data }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {data.map((region, index) => (
      <div key={index} className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{region.province}</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Incidents:</span>
            <span className="font-medium text-red-600">{region.incident_count}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Unique Users:</span>
            <span className="font-medium">{region.unique_users}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Avg Confidence:</span>
            <span className="font-medium">
              {region.avg_confidence > 0 ? `${(region.avg_confidence * 100).toFixed(1)}%` : 'N/A'}
            </span>
          </div>
          <div className="text-xs text-gray-400">
            Center: {region.center_lat.toFixed(3)}, {region.center_lng.toFixed(3)}
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Users Tab Component
const UsersTab = ({ data }) => (
  <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200">
      <h3 className="text-lg font-medium">Flagged Users</h3>
      <p className="text-gray-600">Users with suspicious verification activities</p>
    </div>
    
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flags</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Confidence</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Incident</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((user, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                  <div className="text-sm text-gray-500">@{user.username}</div>
                  <div className="text-xs text-gray-400">{user.email}</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {user.flag_count} flags
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {(user.avg_confidence * 100).toFixed(1)}%
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(user.last_incident).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {user.last_login_address || 'Unknown'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {data.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No flagged users found.</p>
        </div>
      )}
    </div>
  </div>
);

// Email Alerts Tab Component
const EmailAlertsTab = ({ data, refreshData }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [formData, setFormData] = useState({
    email_address: '',
    contact_name: '',
    alert_types: 'suspicious_activity'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingAlert) {
        await api.put(`/api/admin/email-alerts/${editingAlert.id}`, formData);
      } else {
        await api.post('/api/admin/email-alerts', formData);
      }
      
      setShowModal(false);
      setEditingAlert(null);
      setFormData({ email_address: '', contact_name: '', alert_types: 'suspicious_activity' });
      refreshData();
    } catch (error) {
      alert('Failed to save email alert');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    setFormData({
      email_address: alert.email_address,
      contact_name: alert.contact_name,
      alert_types: alert.alert_types
    });
    setShowModal(true);
  };

  const handleToggle = async (alertId, currentStatus) => {
    try {
      await api.post(`/api/admin/email-alerts/${alertId}/toggle`, {
        is_active: !currentStatus
      });
      refreshData();
    } catch (error) {
      alert('Failed to update alert status');
    }
  };

  const handleDelete = async (alertId) => {
    if (!confirm('Are you sure you want to delete this email alert?')) return;
    
    try {
      await api.delete(`/api/admin/email-alerts/${alertId}`);
      refreshData();
    } catch (error) {
      alert('Failed to delete email alert');
    }
  };

  const testEmailService = async () => {
    try {
      // Show loading state
      const button = document.querySelector('[data-test-email]');
      const originalText = button.textContent;
      button.textContent = 'Testing...';
      button.disabled = true;
      
      const response = await api.post('/api/admin/test-email');
      
      if (response.data.success) {
        alert(`✅ Email Test Successful!\n\n${response.data.message}\n\nStatus: ${response.data.status_code || 'OK'}`);
      } else {
        alert(`❌ Email Test Failed!\n\nError: ${response.data.error}\n\nDetails: ${response.data.details || 'No additional details'}`);
      }
      
      // Restore button
      button.textContent = originalText;
      button.disabled = false;
    } catch (error) {
      alert(`❌ Email Test Failed!\n\nNetwork Error: ${error.message}\n\nPlease check if the backend server is running.`);
      
      // Restore button
      const button = document.querySelector('[data-test-email]');
      button.textContent = 'Test Email Service';
      button.disabled = false;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Email Alert Management</h3>
        <div className="space-x-2">
          <button
            onClick={testEmailService}
            data-test-email
            className="inline-flex items-center px-3 py-2 border border-green-500/30 shadow-sm text-sm leading-4 font-mono rounded-md text-green-400 bg-green-600/20 hover:bg-green-600/40 hover:text-green-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300"
          >
            🔧 Test Email Service
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add Email Alert
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email Address
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Alert Types
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((alert) => (
              <tr key={alert.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {alert.contact_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {alert.email_address}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {alert.alert_types}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    alert.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {alert.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(alert.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => handleEdit(alert)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggle(alert.id, alert.is_active)}
                    className={alert.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                  >
                    {alert.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => handleDelete(alert.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {data.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No email alerts configured.</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAlert ? 'Edit Email Alert' : 'Add Email Alert'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                  <input
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  <input
                    type="email"
                    required
                    value={formData.email_address}
                    onChange={(e) => setFormData({...formData, email_address: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Alert Types</label>
                  <select
                    value={formData.alert_types}
                    onChange={(e) => setFormData({...formData, alert_types: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="suspicious_activity">Suspicious Activity</option>
                    <option value="all_alerts">All Alerts</option>
                  </select>
                </div>
                <div className="flex space-x-2 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingAlert(null);
                      setFormData({ email_address: '', contact_name: '', alert_types: 'suspicious_activity' });
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;